import { Box, Typography, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import { LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { RegistrationActions, RegistrationBack } from "@/components/property_registration/RegistrationActions";
import { CustomForm } from "@/components/property_registration/RegistrationInputs";
import { CustomFormLabel } from "@/components/property_registration/RegistrationInputs";
import { CustomSelector } from "@/components/property_registration/RegistrationInputs";
import { DatePickerComponent } from "@/components/property_registration/RegistrationInputs";
import { Toast } from "@/components/Toast";
import { LeaseContractTypeOfTheIncomeOfTheBeginningOfContractOptions } from "@/constants/management";
import theme from "@/theme";
import { PropertyRegistrationState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  content: {
    width: 640,
    [theme.breakpoints.down("sm")]: {
      width: 342,
    },
    margin: "8px  auto 0px auto",
  },
  block: {
    marginBottom: 24,
  },
  blockTitle: {
    color: theme.palette.text.primary,
    marginBottom: 8,
  },
  formBlock: {
    backgroundColor: theme.palette.background.default,
    padding: "8px 16px",
    borderRadius: 8,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      borderBottom: "none",
    },
  },
}));

interface AddNewOwnPropertyCashFlowPageProps {
  onBack: () => void;
  validate: () => Promise<boolean>;
}

export const prefecture_unselected = "";

const AddNewOwnPropertyCashFlowPage: React.FC<AddNewOwnPropertyCashFlowPageProps> = ({ onBack, validate }) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const [formData] = useRecoilState(PropertyRegistrationState);
  const watchValues = useWatch({
    name: ["type_of_the_income_of_the_beginning_of_contract", "starts_at", "starts_at_day", "ends_at"],
    control: control,
  });

  // サーバーエラー
  const [serverError, setSeverError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");

  const toMonthPickerDate = useCallback((value: unknown, fallback: Date | null) => {
    // MEMO: 以前はfallbackに「当月」等を渡して未入力でも自動入力していたが、未入力開始に変更（2026-01-02）
    if (!value) return fallback;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? fallback : value;
    const parsed = dayjs(value as never);
    return parsed.isValid() ? parsed.toDate() : fallback;
  }, []);

  const handleNext = async () => {
    const isValid = await validate();
    const data = getValues();
    if (isValid) {
      console.log("isValid in cashflow");
      console.log(data);
    }
  };

  const handleClose = useCallback(() => {
    setSeverError("");
  }, []);

  useEffect(() => {
    if (dayjs(watchValues.ends_at).isBefore(dayjs(watchValues.starts_at))) {
      setDateError("終了日は開始日より後の日付を選択してください。");
    } else {
      setDateError("");
    }
  }, [watchValues.starts_at, watchValues.ends_at]);

  // 開始日が変更されたら終了日を更新
  useEffect(() => {
    if (watchValues.starts_at && watchValues.starts_at_day) {
      const startsAtDate = dayjs(watchValues.starts_at);
      const startDate = startsAtDate.date(parseInt(watchValues.starts_at_day));
      const endDate = startDate.add(2, "year").subtract(1, "day");

      setValue("ends_at", endDate.toDate());
      setValue("ends_at_day", endDate.format("D"));
    }
  }, [watchValues.starts_at, watchValues.starts_at_day, setValue]);

  // 選択肢
  return (
    <>
      <Box>
        <Box className={classes.content} data-registration-content>
          <Box className={classes.block}>
            <Typography variant="h4" className={classes.blockTitle}>
              入ってくるお金
            </Typography>
            <Box className={classes.formBlock} data-registration-group>
              <Controller
                name="monthly_fee"
                control={control}
                defaultValue={formData.monthly_fee || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="家賃収入"
                    required
                    error={!!errors.monthly_fee}
                    errorText={errors.monthly_fee?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Controller
                name="monthly_management_fee"
                control={control}
                defaultValue={formData.monthly_management_fee || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="管理費収入"
                    required
                    error={!!errors.monthly_management_fee}
                    errorText={errors.monthly_management_fee?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
            </Box>
          </Box>

          <Box className={classes.block}>
            <Typography variant="h4" className={classes.blockTitle}>
              賃貸借契約期間
            </Typography>
            <Box className={classes.formBlock} data-registration-group>
              <Box mb={1}>
                <CustomFormLabel
                  label="賃貸借契約の開始月"
                  required
                  info="購入した年月日を選択してください。"
                  helperText="※一棟の場合は直近で契約更新した部屋の期間を入力"
                />
              </Box>

              <Controller
                name="starts_at"
                control={control}
                // MEMO: 自動で当月を入れない（2026-01-02）
                // defaultValue={toMonthPickerDate(formData.starts_at, dayjs().startOf("month").toDate())}
                defaultValue={toMonthPickerDate(formData.starts_at, null)}
                render={({ ...field }) => (
                  <DatePickerComponent
                    required
                    {...field}
                    labelLight="年月"
                    error={!!errors.starts_at}
                    errorText={errors.starts_at?.message as string}
                    value={toMonthPickerDate(field.value, null)}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="starts_at_day"
                control={control}
                // MEMO: 自動で「1日」を入れない（2026-01-02）
                // defaultValue={formData.starts_at_day || "1"}
                defaultValue={formData.starts_at_day || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    labelLight="日にち"
                    helperText="不明の場合は値を「1日」のままお進みください。"
                    error={!!errors.starts_at_day}
                    errorText={errors.starts_at_day?.message}
                    onChange={field.onChange}
                    placeHolder="1"
                    unitSuffix="日"
                    isShort
                  />
                )}
              />

              <Box mb={1}>
                <CustomFormLabel label="賃貸借契約の終了月" required info="購入した年月日を選択してください。" />
              </Box>

              <Controller
                name="ends_at"
                control={control}
                // MEMO: 自動で「当月+2年」を入れない（2026-01-02）
                // defaultValue={toMonthPickerDate(formData.ends_at, dayjs().add(2, "year").startOf("month").toDate())}
                defaultValue={toMonthPickerDate(formData.ends_at, null)}
                render={({ ...field }) => (
                  <DatePickerComponent
                    required
                    labelLight="年月"
                    {...field}
                    error={!!errors.ends_at}
                    errorText={errors.ends_at?.message as string}
                    value={toMonthPickerDate(field.value, null)}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="ends_at_day"
                control={control}
                // MEMO: 自動で「1日」を入れない（2026-01-02）
                // defaultValue={formData.ends_at_day || "1"}
                defaultValue={formData.ends_at_day || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    labelLight="日にち"
                    helperText="不明の場合は値を「1日」のままお進みください。"
                    error={!!errors.ends_at_day}
                    errorText={errors.ends_at_day?.message}
                    onChange={field.onChange}
                    placeHolder="1"
                    unitSuffix="日"
                    isShort
                  />
                )}
              />
              {dateError && (
                <Typography color="error" variant="body2">
                  {dateError}
                </Typography>
              )}
              <Controller
                name="type_of_the_income_of_the_beginning_of_contract"
                control={control}
                defaultValue={formData.type_of_the_income_of_the_beginning_of_contract || ""}
                render={({ ...field }) => (
                  <CustomSelector
                    {...field}
                    label="賃貸借契約開始時における収入"
                    onChange={field.onChange}
                    error={!!errors.type_of_the_income_of_the_beginning_of_contract}
                    errorText={errors.type_of_the_income_of_the_beginning_of_contract?.message}
                    options={LeaseContractTypeOfTheIncomeOfTheBeginningOfContractOptions}
                  />
                )}
              />
              {/* security_deposit */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.SecurityDeposit && (
                <Controller
                  name="security_deposit"
                  control={control}
                  defaultValue={formData.security_deposit || ""}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="更新料"
                      required
                      error={!!errors.security_deposit}
                      errorText={errors.security_deposit?.message}
                      onChange={field.onChange}
                      placeHolder="1"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}
              {/* 礼金 */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.KeyMoney && (
                <Controller
                  name="key_money"
                  control={control}
                  defaultValue={formData.key_money || ""}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="礼金"
                      required
                      error={!!errors.key_money}
                      errorText={errors.key_money?.message}
                      onChange={field.onChange}
                      placeHolder="1"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}
            </Box>
          </Box>
          <Box className={classes.block}>
            <Typography variant="h4" className={classes.blockTitle}>
              毎月の経費
            </Typography>
            <Box className={classes.formBlock} data-registration-group>
              <Controller
                name="building_management_cost"
                control={control}
                defaultValue={formData.building_management_cost || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="建物管理費"
                    required
                    error={!!errors.building_management_cost}
                    errorText={errors.building_management_cost?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Controller
                name="repair_fund_cost"
                control={control}
                defaultValue={formData.repair_fund_cost || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="修繕積立金"
                    required
                    error={!!errors.repair_fund_cost}
                    errorText={errors.repair_fund_cost?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Controller
                name="management_cost"
                control={control}
                defaultValue={formData.management_cost || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="賃貸管理費"
                    required
                    error={!!errors.management_cost}
                    errorText={errors.management_cost?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Controller
                name="misc_cost"
                control={control}
                defaultValue={formData.misc_cost || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="その他経費"
                    error={!!errors.misc_cost}
                    errorText={errors.misc_cost?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Typography variant="h4">管理会社への手数料</Typography>
              {/* 新規賃貸契約手数料 */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.KeyMoney && (
                <Controller
                  name="outsourcing_fee_rate_of_new_lease_contract"
                  control={control}
                  defaultValue={formData.outsourcing_fee_rate_of_new_lease_contract || ""}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="新規賃貸契約手数料"
                      required
                      error={!!errors.outsourcing_fee_rate_of_new_lease_contract}
                      errorText={errors.outsourcing_fee_rate_of_new_lease_contract?.message}
                      onChange={field.onChange}
                      placeHolder="0.5"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}
              {/* 賃貸契約更新時手数料 */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.SecurityDeposit && (
                <Controller
                  name="outsourcing_fee_rate_of_updating_lease_contract"
                  control={control}
                  defaultValue={formData.outsourcing_fee_rate_of_updating_lease_contract || ""}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="賃貸契約更新時手数料"
                      required
                      error={!!errors.outsourcing_fee_rate_of_updating_lease_contract}
                      errorText={errors.outsourcing_fee_rate_of_updating_lease_contract?.message}
                      onChange={field.onChange}
                      placeHolder="0.5"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}
            </Box>
          </Box>

          <Box className={classes.block}>
            <Typography variant="h4" className={classes.blockTitle}>
              希望売却価格（あれば）
            </Typography>
            <Box className={classes.formBlock} data-registration-group>
              <Controller
                name="asking_price"
                control={control}
                defaultValue={formData.asking_price || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="希望売却価格"
                    error={!!errors.asking_price}
                    errorText={errors.asking_price?.message}
                    onChange={field.onChange}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
        <RegistrationActions>
          <CustomButton type="submit" customVariant="filled" onClick={handleNext}>
            登録する
          </CustomButton>
          <RegistrationBack onClick={onBack} />
        </RegistrationActions>
      </Box>
      <Toast open={!!serverError} variant="error" message={serverError} onClose={handleClose} />
    </>
  );
};

export default AddNewOwnPropertyCashFlowPage;
